import type { Vec2 } from '../shared/types';

export interface SnapResult {
  offset: Vec2;
  at: Vec2 | null;
}

/**
 * Compute an axis-independent snap offset: each of X and Y snaps to the nearest
 * candidate within `tol`. With centres included in both probes and candidates,
 * this aligns centres (and edges/corners) to each other, and yields a coincident
 * snap when both axes lock onto the same point.
 */
export function axisSnapOffset(probes: Vec2[], candidates: Vec2[], tol: number): SnapResult {
  let bestDX = Infinity;
  let bestDY = Infinity;
  let dxv = 0;
  let dyv = 0;
  let atX: Vec2 | null = null;
  let atY: Vec2 | null = null;
  for (const p of probes) {
    for (const c of candidates) {
      const dx = c.x - p.x;
      const dy = c.y - p.y;
      if (Math.abs(dx) <= tol && Math.abs(dx) < bestDX) {
        bestDX = Math.abs(dx);
        dxv = dx;
        atX = c;
      }
      if (Math.abs(dy) <= tol && Math.abs(dy) < bestDY) {
        bestDY = Math.abs(dy);
        dyv = dy;
        atY = c;
      }
    }
  }
  return {
    offset: { x: bestDX < Infinity ? dxv : 0, y: bestDY < Infinity ? dyv : 0 },
    at: atY ?? atX,
  };
}
